import React, { useEffect, useRef, useState } from 'react';

// Custom hook for scroll animations (reusing from App.js)
function useScrollAnimation() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const el = ref.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, []);

  return [ref, isVisible];
}

// Gallery Item Component
function GalleryItem({ item, index, isVisible }) {
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    if (item.socialLink) {
      window.open(item.socialLink, '_blank');
    }
  };

  const isVideo = (filename) => {
    return filename && /\.(mp4|mov|avi|wmv|webm)$/i.test(filename);
  };

  return (
    <div 
      className={`gallery-item scroll-animate-item ${isVisible ? 'animate-in' : ''}`}
      style={{
        animationDelay: `${index * 0.05}s`,
        cursor: item.socialLink ? 'pointer' : 'default'
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      <div className="gallery-item-content">
        {item.media && (
          <div className="gallery-media-container">
            {isVideo(item.media) ? (
              <video 
                src={item.media} 
                alt={item.name}
                className="gallery-media"
                style={{
                  transform: hovered ? 'scale(1.05)' : 'scale(1)',
                  filter: hovered ? 'brightness(1.1)' : 'brightness(1)'
                }}
                muted
                loop
                playsInline
                autoPlay={hovered}
              />
            ) : (
              <img 
                src={item.media} 
                alt={item.name}
                className="gallery-media"
                style={{
                  transform: hovered ? 'scale(1.05)' : 'scale(1)',
                  filter: hovered ? 'brightness(1.1)' : 'brightness(1)'
                }}
              />
            )}
          </div>
        )}
        
        {item.name && (
          <div className="gallery-item-title">
            {item.name}
          </div>
        )}
        
        {item.username && (
          <div className="gallery-item-subtitle">
            @{item.username}
          </div>
        )}
        
        {item.socialLink && (
          <div className="gallery-social-indicator">
            <div className="social-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Full Gallery Page Component
function GalleryPage() {
  const [ref, isVisible] = useScrollAnimation();

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  // ALL 38 media items from the 13 creators
  const allGalleryItems = [
    // Dalo - @dalosin (3 images)
    {
      id: 1,
      name: "Dalo",
      username: "dalosin",
      media: "/gallery/Dalo - @dalosin/FUN v2.png",
      socialLink: "https://x.com/dalosin"
    },
    {
      id: 2,
      name: "Dalo",
      username: "dalosin",
      media: "/gallery/Dalo - @dalosin/FUN v3.png",
      socialLink: "https://x.com/dalosin"
    },
    {
      id: 3,
      name: "Dalo",
      username: "dalosin",
      media: "/gallery/Dalo - @dalosin/FUN v4.png",
      socialLink: "https://x.com/dalosin"
    },
    // Death - @de6ths (2 images)
    {
      id: 4,
      name: "Death",
      username: "de6ths",
      media: "/gallery/Death - @de6ths/E4696-AC2-EDC0-41-FE-9885-A7-A848-B73-EA1.jpg",
      socialLink: "https://x.com/de6ths"
    },
    {
      id: 5,
      name: "Death",
      username: "de6ths",
      media: "/gallery/Death - @de6ths/IMG-3592.jpg",
      socialLink: "https://x.com/de6ths"
    },
    // Fae - @inurefae (3 images)
    {
      id: 6,
      name: "Fae",
      username: "inurefae",
      media: "/gallery/Fae - @inurefae/pV5sIOAp.jpg",
      socialLink: "https://x.com/inurefae"
    },
    {
      id: 7,
      name: "Fae",
      username: "inurefae",
      media: "/gallery/Fae - @inurefae/undefined - Imgur (1).png",
      socialLink: "https://x.com/inurefae"
    },
    {
      id: 8,
      name: "Fae",
      username: "inurefae",
      media: "/gallery/Fae - @inurefae/undefined - Imgur.jpg",
      socialLink: "https://x.com/inurefae"
    },
    // Fruitsplashy - @fruitsplashy (4 items: 2 videos, 2 gifs)
    {
      id: 9,
      name: "Fruitsplashy",
      username: "fruitsplashy",
      media: "/gallery/Fruitsplashy- @fruitsplashy/funcute.mp4",
      socialLink: "https://x.com/fruitsplashy"
    },
    {
      id: 10,
      name: "Fruitsplashy",
      username: "fruitsplashy",
      media: "/gallery/Fruitsplashy- @fruitsplashy/funfun.gif",
      socialLink: "https://x.com/fruitsplashy"
    },
    {
      id: 11,
      name: "Fruitsplashy",
      username: "fruitsplashy",
      media: "/gallery/Fruitsplashy- @fruitsplashy/funnnn.gif",
      socialLink: "https://x.com/fruitsplashy"
    },
    {
      id: 12,
      name: "Fruitsplashy",
      username: "fruitsplashy",
      media: "/gallery/Fruitsplashy- @fruitsplashy/funswingset.mp4",
      socialLink: "https://x.com/fruitsplashy"
    },
    // Jace - @s0ngs (6 items: 5 gifs, 1 image)
    {
      id: 14,
      name: "Jace",
      username: "s0ngs",
      media: "/gallery/Jace - @s0ngs/FUN_jace4.gif",
      socialLink: "https://x.com/s0ngs"
    },
    {
      id: 15,
      name: "Jace",
      username: "s0ngs",
      media: "/gallery/Jace - @s0ngs/jace-fun-1.gif",
      socialLink: "https://x.com/s0ngs"
    },
    {
      id: 16,
      name: "Jace",
      username: "s0ngs",
      media: "/gallery/Jace - @s0ngs/jace-fun-2_2.gif",
      socialLink: "https://x.com/s0ngs"
    },
    {
      id: 17,
      name: "Jace",
      username: "s0ngs",
      media: "/gallery/Jace - @s0ngs/jace-fun-2.gif",
      socialLink: "https://x.com/s0ngs"
    },
    {
      id: 18,
      name: "Jace",
      username: "s0ngs",
      media: "/gallery/Jace - @s0ngs/jace-fun-3.gif",
      socialLink: "https://x.com/s0ngs"
    },
    {
      id: 19,
      name: "Jace",
      username: "s0ngs",
      media: "/gallery/Jace - @s0ngs/txt_00052.png",
      socialLink: "https://x.com/s0ngs"
    },
    // Nomad - @nomadlol (1 gif)
    {
      id: 21,
      name: "Nomad",
      username: "nomadlol",
      media: "/gallery/Nomad - @nomadlol/FUN GIF.gif",
      socialLink: "https://x.com/nomadlol"
    },
    // Prax - @J5K (4 images)
    {
      id: 22,
      name: "Prax",
      username: "J5K",
      media: "/gallery/Prax - @J5K/111111_00000.png",
      socialLink: "https://x.com/J5K"
    },
    {
      id: 23,
      name: "Prax",
      username: "J5K",
      media: "/gallery/Prax - @J5K/Comp 1_00000.png",
      socialLink: "https://x.com/J5K"
    },
    {
      id: 24,
      name: "Prax",
      username: "J5K",
      media: "/gallery/Prax - @J5K/dcthing.png",
      socialLink: "https://x.com/J5K"
    },
    {
      id: 25,
      name: "Prax",
      username: "J5K",
      media: "/gallery/Prax - @J5K/discordcrt.png",
      socialLink: "https://x.com/J5K"
    },
    // Qomic - @qeyote (1 image)
    {
      id: 26,
      name: "Qomic",
      username: "qeyote",
      media: "/gallery/Qomic - @qeyote/coboh.png",
      socialLink: "https://x.com/qeyote"
    },
    // Rayro - @digishawty (2 gifs)
    {
      id: 27,
      name: "Rayro",
      username: "digishawty",
      media: "/gallery/Rayro - @digishawty/funxxx.gif",
      socialLink: "https://x.com/digishawty"
    },
    {
      id: 28,
      name: "Rayro",
      username: "digishawty",
      media: "/gallery/Rayro - @digishawty/one.gif",
      socialLink: "https://x.com/digishawty"
    },
    // Rob - @robm0de (4 images)
    {
      id: 31,
      name: "Rob",
      username: "robm0de",
      media: "/gallery/Rob - @robm0de/dabgoFront.jpg",
      socialLink: "https://x.com/robm0de"
    },
    {
      id: 32,
      name: "Rob",
      username: "robm0de",
      media: "/gallery/Rob - @robm0de/funMascot2_bw2.png",
      socialLink: "https://x.com/robm0de"
    },
    {
      id: 33,
      name: "Rob",
      username: "robm0de",
      media: "/gallery/Rob - @robm0de/funPoster_Tex.jpg",
      socialLink: "https://x.com/robm0de"
    },
    {
      id: 34,
      name: "Rob",
      username: "robm0de",
      media: "/gallery/Rob - @robm0de/rosinFront.jpg",
      socialLink: "https://x.com/robm0de"
    },
    // Yam - @yamoien (3 images)
    {
      id: 35,
      name: "Yam",
      username: "yamoien",
      media: "/gallery/Yam - @yamoien/B82647-BE-ED79-4387-980-D-6-C59199-D3-ED5.jpg",
      socialLink: "https://x.com/yamoien"
    },
    {
      id: 36,
      name: "Yam",
      username: "yamoien",
      media: "/gallery/Yam - @yamoien/FÜN AVI.jpg",
      socialLink: "https://x.com/yamoien"
    },
    {
      id: 37,
      name: "Yam",
      username: "yamoien",
      media: "/gallery/Yam - @yamoien/v2.png",
      socialLink: "https://x.com/yamoien"
    },
    // Yoffdog - @yoffdog0 (1 image)
    {
      id: 38,
      name: "Yoffdog",
      username: "yoffdog0",
      media: "/gallery/Yoffdog - @yoffdog0/FUNPOSTERBLUEDIGITAL.jpg",
      socialLink: "https://x.com/yoffdog0"
    }
  ];

  return (
    <div style={{ backgroundColor: '#fff', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <nav style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <a href="/" style={{
          fontSize: '24px',
          fontWeight: '700',
          fontFamily: 'Benzin, Inter, Arial, sans-serif',
          textDecoration: 'none',
          color: '#000',
          letterSpacing: '0.05em'
        }}>
          FÜN
        </a>
        <a href="/" style={{
          fontSize: '16px',
          fontWeight: '500',
          textDecoration: 'none',
          color: '#666',
          transition: 'color 0.3s ease'
        }}>
          ← Back to Home
        </a>
      </nav>

      {/* Gallery Section */}
      <section 
        ref={ref}
        className={`gallery-section scroll-animate ${isVisible ? 'animate-in' : ''}`}
        style={{
          backgroundColor: '#fff',
          padding: '100px 48px',
          width: '100%',
          maxWidth: '100vw',
          boxSizing: 'border-box'
        }}
      >
        <div className="gallery-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Gallery Header */}
          <div className={`scroll-animate-content ${isVisible ? 'animate-in' : ''}`} style={{
            textAlign: 'center',
            marginBottom: '64px'
          }}>
            <h1 style={{
              fontSize: '4rem',
              fontWeight: '700',
              margin: '0 0 16px 0',
              color: '#222',
              fontFamily: 'Benzin, Inter, Arial, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              FULL GALLERY
            </h1>
            <p style={{
              fontSize: '1.2rem',
              color: '#666',
              margin: '0 0 8px 0',
              fontWeight: '500'
            }}>
              {allGalleryItems.length} pieces from our creative community
            </p>
            <p style={{
              fontSize: '1rem',
              color: '#999',
              margin: '0'
            }}>
              Click any creator to visit their Twitter
            </p>
          </div>

          {/* Gallery Grid */}
          <div className={`scroll-animate-grid ${isVisible ? 'animate-in' : ''}`} style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            width: '100%',
            justifyContent: 'center'
          }}>
            {allGalleryItems.map((item, index) => (
              <GalleryItem 
                key={item.id} 
                item={item} 
                index={index}
                isVisible={isVisible}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default GalleryPage;

